import { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import { EditorUIConfig } from 'types';
// Import Store
import { useAppDispatch } from 'store';
import { changeTemplateValue, setUIConfig } from 'store/slices/template';
// Import Hooks
import { useValidator } from 'hooks';
// Import Components
import ViberCascadeContent from './ViberCascadeContent';
import TelegramTemplateContent from './Telegram/TelegramTemplateContent';
import HooryTemplateContent from './HooryTemplateContent';
import DynamicPopUpContent from './DynamicPopUpContent';
import EmailTemplateContent from './EmailTemplateContent';
import InternalTemplateContent from './InternalTemplateContent';
import PushTemplateContent from './PushTemplateContent';
import SMSTemplateContent from './SMSTemplateContent';
import WhatsAppTemplateContent from './WhatsApp/WhatsAppTemplateContent';
import WhatsAppProviderCascadeContent from './WhatsAppProviderCascadeContent';
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Services
import { getTemplateValidationTranslatableErrorText } from 'services/template';

const CommonTemplateContent = (props) => {
    const {
        dynamicVariables,
        templateLangKey,
        templateType,
        templateData,
        maxSMSPartsCount,
        shortenerAndTrackerSettings,
        getAssets,
        addAsset,
        eventId,
    } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const validator = useValidator(getTemplateValidationTranslatableErrorText(t));

    const isSMSTemplate = templateType === TemplateTypeEnum.SMS;
    const isHooryTemplate = templateType === TemplateTypeEnum.Hoory;
    const isInternalTemplate = templateType === TemplateTypeEnum.Internal;
    const isViberTemplate = templateType === TemplateTypeEnum.Viber;
    const isWhatsAppTemplate = templateType === TemplateTypeEnum.WhatsApp;
    const isWhatsAppProviderTemplate = templateType === TemplateTypeEnum.WhatsAppProvider;
    const isTelegramTemplate = templateType === TemplateTypeEnum.Telegram;
    const isEmailTemplate = templateType === TemplateTypeEnum.Email;
    const isDynamicPopUpTemplate = templateType === TemplateTypeEnum.DynamicPopUp;
    const isPushNotificationTemplate = templateType === TemplateTypeEnum.PushNotification;

    const inputIntervalTokenRef = useRef<Nullable<NodeJS.Timeout>>(null);

    const handleChange = useCallback(
        (propertyName, value, changeTimeout = 300) => {
            if (inputIntervalTokenRef.current) {
                clearTimeout(inputIntervalTokenRef.current);
            }

            if (changeTimeout === 0) {
                dispatch(changeTemplateValue({ propertyName, value, templateLangKey }));
                validator({ [templateLangKey + propertyName]: value });
            } else {
                inputIntervalTokenRef.current = setTimeout(() => {
                    dispatch(changeTemplateValue({ propertyName, value, templateLangKey }));
                    validator({ [templateLangKey + propertyName]: value });
                }, changeTimeout);
            }
        },
        [dispatch, templateLangKey, validator],
    );

    const handleConfigChange = useCallback(
        (editorKey: string, editorConfig: EditorUIConfig) => {
            dispatch(
                setUIConfig({
                    templateLangKey,
                    editorKey,
                    editorConfig,
                }),
            );
        },
        [dispatch, templateLangKey],
    );

    return (
        <div className="crm-scroll-holder sms-template">
            <div className="crm-scrollable-container">
                <div
                    className={classNames('crm-create-template-page-c-container', {
                        viber: isViberTemplate,
                        telegram: isTelegramTemplate,
                        sms: isSMSTemplate,
                        push: isPushNotificationTemplate,
                        email: isEmailTemplate,
                        hoory: isHooryTemplate,
                        'dynamic-pop-up': isDynamicPopUpTemplate,
                    })}
                >
                    {isPushNotificationTemplate && (
                        <PushTemplateContent
                            templateData={templateData}
                            handleChange={handleChange}
                            templateLangKey={templateLangKey}
                            dynamicVariables={dynamicVariables}
                            shortenerAndTrackerSettings={shortenerAndTrackerSettings}
                        />
                    )}
                    {isEmailTemplate && (
                        <EmailTemplateContent
                            templateData={templateData}
                            handleChange={handleChange}
                            handleUIConfigChange={handleConfigChange}
                            templateLangKey={templateLangKey}
                            getAssets={getAssets}
                            addAsset={addAsset}
                            dynamicVariables={dynamicVariables}
                            eventId={eventId}
                        />
                    )}
                    {isInternalTemplate && (
                        <InternalTemplateContent
                            templateData={templateData}
                            handleChange={handleChange}
                            handleUIConfigChange={handleConfigChange}
                            templateLangKey={templateLangKey}
                            getAssets={getAssets}
                            addAsset={addAsset}
                            dynamicVariables={dynamicVariables}
                            eventId={eventId}
                        />
                    )}
                    {isDynamicPopUpTemplate && (
                        <DynamicPopUpContent
                            templateData={templateData}
                            handleChange={handleChange}
                            handleUIConfigChange={handleConfigChange}
                            templateLangKey={templateLangKey}
                            getAssets={getAssets}
                            addAsset={addAsset}
                            dynamicVariables={dynamicVariables}
                            eventId={eventId}
                        />
                    )}
                    {isWhatsAppTemplate && (
                        <WhatsAppTemplateContent
                            templateData={templateData}
                            templateLangKey={templateLangKey}
                            handleChange={handleChange}
                            dynamicVariables={dynamicVariables}
                        />
                    )}
                    {isWhatsAppProviderTemplate && (
                        <WhatsAppProviderCascadeContent
                            templateData={templateData}
                            templateLangKey={templateLangKey}
                            handleChange={handleChange}
                            dynamicVariables={dynamicVariables}
                            maxSMSPartsCount={maxSMSPartsCount}
                            shortenerAndTrackerSettings={shortenerAndTrackerSettings}
                        />
                    )}
                    {isViberTemplate && (
                        <ViberCascadeContent
                            templateData={templateData}
                            templateLangKey={templateLangKey}
                            handleChange={handleChange}
                            dynamicVariables={dynamicVariables}
                            maxSMSPartsCount={maxSMSPartsCount}
                            shortenerAndTrackerSettings={shortenerAndTrackerSettings}
                        />
                    )}
                    {isTelegramTemplate && (
                        <TelegramTemplateContent
                            templateData={templateData}
                            templateLangKey={templateLangKey}
                            handleChange={handleChange}
                            dynamicVariables={dynamicVariables}
                            shortenerAndTrackerSettings={shortenerAndTrackerSettings}
                        />
                    )}
                    {isSMSTemplate && (
                        <SMSTemplateContent
                            templateData={templateData}
                            templateLangKey={templateLangKey}
                            handleChange={handleChange}
                            dynamicVariables={dynamicVariables}
                            maxSMSPartsCount={maxSMSPartsCount}
                            shortenerAndTrackerSettings={shortenerAndTrackerSettings}
                            eventId={eventId}
                        />
                    )}
                    {isHooryTemplate && (
                        <HooryTemplateContent
                            templateData={templateData}
                            templateLangKey={templateLangKey}
                            handleChange={handleChange}
                            dynamicVariables={dynamicVariables}
                            shortenerAndTrackerSettings={shortenerAndTrackerSettings}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

CommonTemplateContent.propTypes = {
    templateBody: PropTypes.any,
    dynamicVariables: PropTypes.arrayOf(PropTypes.object),
    templateLangKey: PropTypes.string.isRequired,
    templateType: PropTypes.oneOf(Object.values(TemplateTypeEnum)),
    templateData: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    maxSMSPartsCount: PropTypes.number,
    getAssets: PropTypes.func,
    addAsset: PropTypes.func,
    shortenerAndTrackerSettings: PropTypes.array,
    eventId: PropTypes.number,
};

CommonTemplateContent.defaultProps = {
    templateType: TemplateTypeEnum.SMS,
    templateBody: '',
    getAssets: noop,
    addAsset: noop,
    shortenerAndTrackerSettings: [],
};

export default CommonTemplateContent;
